import type { FeatureDefinition, PlanInfo } from '@genealogy-app/shared';
import apiClient from './client';

// ─── Stammbäume (Mandanten) ─────────────────────────────────────────────────

export interface Tree {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  personCount: number;
  /** Das Familienwappen, falls eins hinterlegt ist. Sonst null. */
  wappenUrl: string | null;
  /** Besitze ICH diesen Baum? Entscheidet, ob die Mitgliederverwaltung erscheint. */
  isOwner: boolean;
}

// ─── Wappen ─────────────────────────────────────────────────────────────────

/** Lädt ein Wappen hoch und ersetzt ein vorhandenes. */
export async function setzeWappen(treeId: string, datei: File): Promise<{ wappenUrl: string }> {
  const formData = new FormData();
  formData.append('file', datei);
  const { data } = await apiClient.put<{ wappenUrl: string }>(
    `/trees/${treeId}/wappen`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 },
  );
  return data;
}

export async function loescheWappen(treeId: string): Promise<void> {
  await apiClient.delete(`/trees/${treeId}/wappen`);
}

export interface TreeInput {
  name: string;
  description?: string | null;
}

export interface TreeQuota {
  /** Wie viele eigene Stammbäume erlaubt sind. */
  limit: number;
  /** Wie viele davon belegt sind. */
  used: number;
  remaining: number;
  /** true, wenn für dieses Konto eine Ausnahme hinterlegt ist – sie schlägt den Tarif. */
  individuell: boolean;
  /** Name des Tarifs, aus dem die Grenze stammt. */
  tarif: string;
  /** Betreiber unterliegen keinem Kontingent. */
  unbegrenzt: boolean;
}

/** Wie viele Stammbäume darf ich noch anlegen? */
export async function fetchTreeQuota(): Promise<TreeQuota> {
  const { data } = await apiClient.get<TreeQuota>('/trees/quota');
  return data;
}

// ─── Tarif und Speicher ─────────────────────────────────────────────────────

export interface MeinTarif {
  plan: PlanInfo;
  kontingent: TreeQuota;
  /** Alle Funktionen der Installation, mit Beschriftung – auch die gesperrten. */
  features: FeatureDefinition[];
}

/** Mein eigener Tarif: Grenzen und freigeschaltete Funktionen. */
export async function fetchMeinTarif(): Promise<MeinTarif> {
  const { data } = await apiClient.get<MeinTarif>('/trees/my-plan');
  return data;
}

export interface BaumFunktionen {
  /** Name des Tarifs, aus dem die Freischaltung stammt (der des Besitzers). */
  tarif: string;
  /** Freigeschaltete Funktionen (Schlüssel aus FEATURES). */
  features: string[];
  /** Betreiber umgehen die Tarifprüfung – dann steht hier alles drin. */
  istBetreiber: boolean;
}

/** Was im aktiven Stammbaum freigeschaltet ist – Tarif des Besitzers. */
export async function fetchBaumFunktionen(treeId: string): Promise<BaumFunktionen> {
  const { data } = await apiClient.get<BaumFunktionen>(`/trees/${treeId}/features`);
  return data;
}

export interface Personenstand {
  anzahl: number;
  /** null = unbegrenzt. */
  grenze: number | null;
  frei: number | null;
  prozent: number | null;
}

export interface Speicherstand {
  /** Belegt in diesem Baum. */
  belegtBytes: number;
  /** Belegt über alle Bäume desselben Besitzers – daran misst sich die Grenze. */
  belegtGesamtBytes: number;
  /** Wie viele Bäume in die Summe eingehen. */
  baeume: number;
  /** null = unbegrenzt. */
  grenzeBytes: number | null;
  freiBytes: number | null;
  /** 0–100, null bei unbegrenzt. */
  prozent: number | null;
  /** Name des Tarifs, aus dem die Grenze stammt (der des Besitzers). */
  tarif: string;
  /** Personen in diesem Baum und die Grenze dafür. */
  personen: Personenstand;
}

/** Wie voll ist das Archiv dieses Stammbaums? */
export async function fetchSpeicherstand(treeId: string): Promise<Speicherstand> {
  const { data } = await apiClient.get<Speicherstand>(`/trees/${treeId}/storage`);
  return data;
}

/** Bäume, auf die der angemeldete Nutzer Zugriff hat (Admins: alle). */
export async function listTrees(): Promise<Tree[]> {
  const { data } = await apiClient.get<{ data: Tree[] }>('/trees');
  return data.data;
}

export async function createTree(input: TreeInput): Promise<Tree> {
  const { data } = await apiClient.post<Tree>('/trees', input);
  return data;
}

export async function updateTree(id: string, input: Partial<TreeInput>): Promise<Tree> {
  const { data } = await apiClient.patch<Tree>(`/trees/${id}`, input);
  return data;
}

/** Löscht einen Stammbaum samt Inhalt – erfordert das Bestätigungswort. */
export async function deleteTree(id: string): Promise<void> {
  await apiClient.delete(`/trees/${id}`, { data: { confirm: 'LÖSCHEN' } });
}
